import {
    FinancialAccountSourceType,
    type FinancialAccountBase,
} from "@common/types/financialAccount";
import {
    ConnectionProviderType,
    type FinancialConnectionBase,
} from "@common/types/financialConnection";
import {
    ConnectionProvider,
    type IntegrationConnectionBase,
} from "@common/types/integrationConnection";
import type { PlaidConnection } from "@common/types/plaidConnection";
import type { InstitutionKey } from "@design-system/InstitutionIcon/InstitutionIcon";
import type { InstitutionIconResolution } from "@design-system/InstitutionIcon/ResolvedInstitutionIcon";

/** The account fields the logo depends on. */
export type InstitutionIconAccount = Pick<
    FinancialAccountBase,
    "sourceType" | "institutionName"
>;

/** The connection fields the logo depends on; Plaid connections carry `bankLogoUrl`. */
export type InstitutionIconConnection =
    | (Pick<FinancialConnectionBase, "connectionProvider"> &
          Partial<Pick<PlaidConnection, "bankLogoUrl">>)
    | Pick<IntegrationConnectionBase, "provider" | "institutionLogoUrl">;

/** `null` means no known logo: `InstitutionIcon` shows its bank glyph. */
export type ResolvedInstitutionIcon = InstitutionIconResolution;

/** The brand for each integration provider, e.g. a settings connection header. */
export const CONNECTION_PROVIDER_INSTITUTIONS: Record<
    ConnectionProvider,
    InstitutionKey
> = {
    [ConnectionProvider.PAYPAL]: "paypal",
    [ConnectionProvider.MERCURY]: "mercury",
    [ConnectionProvider.QUICKBOOKS]: "quickbooks",
    [ConnectionProvider.BILLCOM]: "bill",
    [ConnectionProvider.GUSTO]: "gusto",
    [ConnectionProvider.STRIPE]: "stripe",
    [ConnectionProvider.ADP_RUN]: "adp",
};

const SOURCE_TYPE_INSTITUTIONS: Partial<
    Record<FinancialAccountSourceType, InstitutionKey>
> = {
    [FinancialAccountSourceType.STRIPE]: "stripe",
    [FinancialAccountSourceType.GUSTO]: "gusto",
    [FinancialAccountSourceType.ADP_RUN]: "adp",
    [FinancialAccountSourceType.RAMP]: "ramp",
    [FinancialAccountSourceType.MERCURY]: "mercury",
    [FinancialAccountSourceType.PAYPAL]: "paypal",
    [FinancialAccountSourceType.BILLCOM]: "bill",
};

// Exact `institutionName` values as stored on accounts; no fuzzy matching.
const INSTITUTION_NAMES = new Map<string, InstitutionKey>([
    ["Chase", "chase"],
    ["Bank of America", "bank-of-america"],
    ["Wells Fargo", "wells-fargo"],
    ["Citibank", "citi"],
    ["Capital One", "capital-one"],
    ["American Express", "amex"],
    ["US Bank", "us-bank"],
    ["Truist", "truist"],
    ["Ally", "ally"],
    ["Goldman Sachs", "goldman-sachs"],
    ["Discover", "discover"],
    ["Regions Bank", "regions-bank"],
    ["KeyBank", "keybank"],
    ["Citizens Bank", "citizens-bank"],
    ["Huntington Bank", "huntington-bank"],
    ["Fifth Third Bank", "fifth-third-bank"],
    ["BMO", "bmo"],
    ["M&T Bank", "mt-bank"],
    ["Silicon Valley Bank", "silicon-valley-bank"],
    ["Morgan Stanley", "morgan-stanley"],
    ["HSBC", "hsbc"],
]);

/** The catalogue bank for an exact bank name, e.g. one detected on an imported account. */
export const getInstitutionForName = (
    institutionName: string | null,
): InstitutionKey | undefined =>
    institutionName === null
        ? undefined
        : INSTITUTION_NAMES.get(institutionName);

const resolveAccount = ({
    sourceType,
    institutionName,
}: InstitutionIconAccount): ResolvedInstitutionIcon => {
    const sourceInstitution = SOURCE_TYPE_INSTITUTIONS[sourceType];
    if (sourceInstitution) {
        return { institution: sourceInstitution };
    }
    if (sourceType === FinancialAccountSourceType.MANUAL) {
        return { fallback: "manual" };
    }
    const namedInstitution = getInstitutionForName(institutionName);
    return namedInstitution ? { institution: namedInstitution } : null;
};

/**
 * Which logo an account shows. A Plaid or integration connection decides on
 * its own (its logo URL, else the integration's provider); any other
 * connection, or none, falls back to the account's source type, then a
 * manual-account glyph, then an exact bank-name match.
 */
export const resolveInstitutionIcon = ({
    account,
    connection,
}: {
    account: InstitutionIconAccount;
    connection?: InstitutionIconConnection | null;
}): ResolvedInstitutionIcon => {
    if (
        connection &&
        "connectionProvider" in connection &&
        connection.connectionProvider === ConnectionProviderType.PLAID
    ) {
        return connection.bankLogoUrl ? { src: connection.bankLogoUrl } : null;
    }
    if (connection && "provider" in connection) {
        if (connection.institutionLogoUrl) {
            return { src: connection.institutionLogoUrl };
        }
        const providerInstitution: InstitutionKey | undefined =
            CONNECTION_PROVIDER_INSTITUTIONS[connection.provider];
        return providerInstitution
            ? { institution: providerInstitution }
            : null;
    }
    return resolveAccount(account);
};
